// ANANT SMART VAULT — 100% client-side AES-GCM encrypted storage
// Data NEVER leaves the browser. Backend only handles subscription + Affiliate Network.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Sparkles, Loader2, Lock, Unlock, Plus, Trash2, Eye, EyeOff, Copy, Shield, Zap, FileText, Share2, Download, KeyRound } from 'lucide-react';
import api from '../../utils/api';
import ATWalletBadge from '../../components/ATWalletBadge';

// Storage keys
const LS_ITEMS = 'vault:items';         // encrypted blob (secrets)
const LS_DOCS = 'vault:docs';           // encrypted blob (document images — separate for size)
const LS_SALT = 'vault:salt';           // base64 salt
const LS_CANARY = 'vault:canary';       // encrypted known plaintext to verify PIN on unlock

// Document type presets (DigiLocker-style)
const DOC_TYPES = [
  { id: 'aadhaar',  label: 'Aadhaar Card',     emoji: '🪪' },
  { id: 'pan',      label: 'PAN Card',         emoji: '💳' },
  { id: 'voter',    label: 'Voter ID',         emoji: '🗳️' },
  { id: 'passport', label: 'Passport',         emoji: '📘' },
  { id: 'driving',  label: 'Driving License',  emoji: '🪪' },
  { id: 'national', label: 'National ID',      emoji: '🆔' },
  { id: 'passbook', label: 'Bank Passbook',    emoji: '📗' },
  { id: 'selfie',   label: 'Selfie / Photo',   emoji: '🤳' },
  { id: 'other',    label: 'Other Document',   emoji: '📎' },
];

// AES-GCM helpers via Web Crypto API
const enc = new TextEncoder();
const dec = new TextDecoder();

const b64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const fromB64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

async function deriveKey(passphrase, salt) {
  const baseKey = await crypto.subtle.importKey('raw', enc.encode(passphrase), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: 250000, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

async function encryptJson(obj, key) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(JSON.stringify(obj)));
  return { iv: b64(iv), ct: b64(ct) };
}

async function decryptJson(payload, key) {
  const iv = fromB64(payload.iv);
  const ct = fromB64(payload.ct);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
  return JSON.parse(dec.decode(pt));
}

export default function SmartVault() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [passphrase, setPassphrase] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [key, setKey] = useState(null);
  const [items, setItems] = useState([]);
  const [docs, setDocs] = useState([]);
  const [tab, setTab] = useState('secrets');
  const [showValue, setShowValue] = useState({});
  // New item form
  const [newTitle, setNewTitle] = useState('');
  const [newCategory, setNewCategory] = useState('Crypto Seed');
  const [newValue, setNewValue] = useState('');
  // New doc form
  const [newDocTitle, setNewDocTitle] = useState('');
  const [newDocType, setNewDocType] = useState('aadhaar');
  const [newDocImg, setNewDocImg] = useState(null);   // dataURL
  const [previewDoc, setPreviewDoc] = useState(null);

  const activeSub = config?.subscription?.active;
  // A vault EXISTS if the salt is stored (SALT is written once at creation time; ITEMS/DOCS may be empty for new vault)
  const hasVault = !!localStorage.getItem(LS_SALT);
  const CATEGORIES = ['Crypto Seed', 'Password', 'Note', 'Bill / Receipt', 'Recovery Key', 'Other'];

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/vault/config');
        setConfig(r.data);
        // Check admin reset request
        try {
          const rs = await api.get('/vault/reset-status');
          if (rs.data?.reset_pending) {
            localStorage.removeItem(LS_ITEMS);
            localStorage.removeItem(LS_DOCS);
            localStorage.removeItem(LS_SALT);
            localStorage.removeItem(LS_CANARY);
            await api.post('/vault/reset-consume', {});
            toast.warning('⚠️ Admin ne aap ka vault reset kar diya hai. Fresh vault banao. Purana data delete ho gaya.');
          }
        } catch (_) { /* ignore */ }
      } catch (e) {
        if (e?.response?.status === 401) navigate('/login');
      } finally { setLoading(false); }
    })();
  }, [navigate]);

  const buyPlan = async (planId) => {
    if (!window.confirm(`${planId === 'monthly' ? '150 AT' : '1500 AT'} deduct hoga aur 7-level Affiliate Network me distribute hoga.`)) return;
    setSubscribing(true);
    try {
      const r = await api.post('/vault/subscribe', { plan_id: planId });
      toast.success(r.data.message);
      const cfg = await api.get('/vault/config');
      setConfig(cfg.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Subscribe failed'); }
    finally { setSubscribing(false); }
  };

  const unlockVault = async () => {
    if (!passphrase || passphrase.length < 4) { toast.error('PIN / passphrase must be at least 4 chars'); return; }
    try {
      let saltB64 = localStorage.getItem(LS_SALT);
      let salt;
      const isFirstTime = !saltB64;
      if (isFirstTime) {
        // Create vault flow — require confirmation
        if (!confirmPass || confirmPass !== passphrase) { toast.error("Passphrases don't match"); return; }
        salt = crypto.getRandomValues(new Uint8Array(16));
        localStorage.setItem(LS_SALT, b64(salt.buffer));
      } else {
        salt = fromB64(saltB64);
      }
      const k = await deriveKey(passphrase, salt);
      // Canary check for existing vault — verifies PIN correctness even when items are empty
      if (isFirstTime) {
        const canaryEnc = await encryptJson({ magic: 'ANANT_VAULT_OK', v: 1 }, k);
        localStorage.setItem(LS_CANARY, JSON.stringify(canaryEnc));
      } else {
        const canaryRaw = localStorage.getItem(LS_CANARY);
        if (canaryRaw) {
          try {
            const dec = await decryptJson(JSON.parse(canaryRaw), k);
            if (!dec || dec.magic !== 'ANANT_VAULT_OK') throw new Error('bad canary');
          } catch {
            toast.error('❌ Wrong PIN / passphrase');
            return;
          }
        } else {
          // Legacy vault (pre-canary) — write canary now for future checks
          const canaryEnc = await encryptJson({ magic: 'ANANT_VAULT_OK', v: 1 }, k);
          localStorage.setItem(LS_CANARY, JSON.stringify(canaryEnc));
        }
      }
      // Decrypt items
      const raw = localStorage.getItem(LS_ITEMS);
      if (raw) {
        try {
          const decrypted = await decryptJson(JSON.parse(raw), k);
          setItems(decrypted);
        } catch {
          toast.error('Vault data corrupted or wrong PIN');
          return;
        }
      } else {
        setItems([]);
      }
      // Decrypt docs
      const rawDocs = localStorage.getItem(LS_DOCS);
      if (rawDocs) {
        try {
          const dArr = await decryptJson(JSON.parse(rawDocs), k);
          setDocs(dArr);
        } catch {
          setDocs([]);
        }
      } else {
        setDocs([]);
      }
      setKey(k);
      toast.success(isFirstTime ? '✅ Vault created & unlocked' : '🔓 Vault unlocked');
    } catch (e) { toast.error('Unlock failed: ' + (e.message || 'unknown')); }
  };

  const lockVault = () => { setKey(null); setItems([]); setDocs([]); setPassphrase(''); setConfirmPass(''); toast.info('🔒 Vault locked'); };

  const persist = useCallback(async (arr) => {
    if (!key) return;
    const encV = await encryptJson(arr, key);
    localStorage.setItem(LS_ITEMS, JSON.stringify(encV));
  }, [key]);

  const persistDocs = useCallback(async (arr) => {
    if (!key) return;
    const encV = await encryptJson(arr, key);
    try {
      localStorage.setItem(LS_DOCS, JSON.stringify(encV));
    } catch (err) {
      toast.error('Storage full — delete some docs first (localStorage ~5-10MB limit)');
    }
  }, [key]);

  const addItem = async () => {
    if (!newTitle.trim() || !newValue.trim()) { toast.error('Fill title + value'); return; }
    const arr = [...items, { id: crypto.randomUUID(), title: newTitle.trim(), category: newCategory, value: newValue, created_at: new Date().toISOString() }];
    setItems(arr);
    await persist(arr);
    setNewTitle(''); setNewValue('');
    toast.success('Saved securely');
  };

  const delItem = async (id) => {
    if (!window.confirm('Delete this vault item?')) return;
    const arr = items.filter((i) => i.id !== id);
    setItems(arr);
    await persist(arr);
    toast.success('Deleted');
  };

  const copyValue = (v) => { navigator.clipboard.writeText(v).then(() => toast.success('Copied')); };

  // ── Document handlers ──
  const onPickDocImg = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error('Max 3 MB per doc (localStorage limit)'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setNewDocImg(ev.target.result);
    reader.readAsDataURL(f);
  };
  const addDoc = async () => {
    if (!newDocTitle.trim() || !newDocImg) { toast.error('Fill title + upload image'); return; }
    const arr = [...docs, { id: crypto.randomUUID(), title: newDocTitle.trim(), doc_type: newDocType, image: newDocImg, created_at: new Date().toISOString() }];
    setDocs(arr);
    await persistDocs(arr);
    setNewDocTitle(''); setNewDocImg(null);
    toast.success('Document saved (encrypted)');
  };
  const delDoc = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    const arr = docs.filter((d) => d.id !== id);
    setDocs(arr);
    await persistDocs(arr);
    toast.success('Deleted');
  };
  const shareDoc = async (d) => {
    try {
      const blob = await (await fetch(d.image)).blob();
      const file = new File([blob], `${d.title.replace(/\s+/g, '-')}.${(blob.type.split('/')[1] || 'png')}`, { type: blob.type });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: d.title });
        return;
      }
    } catch (e) { if (e.name === 'AbortError') return; }
    // Fallback download
    const a = document.createElement('a');
    a.href = d.image;
    a.download = `${d.title.replace(/\s+/g, '-')}.png`;
    a.click();
    toast.info('Downloaded — attach in WhatsApp/email');
  };
  const downloadDoc = (d) => {
    const a = document.createElement('a');
    a.href = d.image;
    a.download = `${d.title.replace(/\s+/g, '-')}.png`;
    a.click();
  };

  const grouped = useMemo(() => {
    const g = {};
    items.forEach((i) => { (g[i.category] = g[i.category] || []).push(i); });
    return g;
  }, [items]);

  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-fuchsia-400" /></div>;

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-fuchsia-950 via-slate-900 to-black text-white">
      <div className="sticky top-16 z-40 bg-slate-950 backdrop-blur-xl border-b-2 border-fuchsia-400/50 px-4 py-2 flex items-center justify-between shadow-2xl shadow-black">
        <Link to="/" className="text-xs text-slate-400 hover:text-white">← Home</Link>
        <ATWalletBadge compact />
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-fuchsia-500/20 border border-fuchsia-400/40 text-[10px] font-black tracking-widest text-fuchsia-200 mb-3">
            <Shield className="w-3 h-3" /> AES-256 · 100% OFFLINE · ZERO SERVER
          </div>
          <h1 className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-fuchsia-300 via-purple-300 to-pink-300 bg-clip-text text-transparent">Anant Smart Vault</h1>
          <p className="text-sm text-slate-300 mt-2 max-w-2xl mx-auto">Crypto seeds · Passwords · Bills · Notes — all encrypted in your browser. <b className="text-fuchsia-300">Not even we can read them.</b></p>
        </div>

        {!activeSub ? (
          <div className="rounded-3xl border-2 border-fuchsia-400/40 bg-gradient-to-br from-fuchsia-950/40 to-purple-950/40 p-6" data-testid="vault-paywall">
            <div className="flex items-center gap-2 mb-3"><Lock className="w-5 h-5 text-fuchsia-400" /><div className="text-lg font-black">Subscribe to Unlock Vault</div></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(config?.plans || []).map((p) => (
                <button key={p.id} data-testid={`vault-plan-${p.id}`} disabled={subscribing} onClick={() => buyPlan(p.id)}
                  className={`text-left rounded-2xl border-2 p-4 ${p.id === 'yearly' ? 'border-emerald-400/60 bg-emerald-900/30' : 'border-fuchsia-400/50 bg-fuchsia-900/30'}`}>
                  <div className="flex items-center justify-between"><div className="text-xs opacity-70 uppercase">{p.label}</div>{p.id === 'yearly' && <div className="text-[9px] font-black px-2 py-0.5 rounded-full bg-emerald-500 text-black">BEST</div>}</div>
                  <div className="text-3xl font-black mt-1">{p.amount} <span className="text-lg opacity-70">AT</span></div>
                  <div className="text-xs opacity-80 mt-1">{p.highlight} · {p.days} days</div>
                  <div className="mt-3 text-xs font-black text-fuchsia-200 flex items-center gap-1"><Zap className="w-3 h-3" /> Subscribe</div>
                </button>
              ))}
            </div>
          </div>
        ) : !key ? (
          <div className="rounded-2xl border-2 border-fuchsia-400/40 bg-fuchsia-500/10 p-6 space-y-3" data-testid="vault-unlock">
            <div className="text-lg font-black text-fuchsia-300 flex items-center gap-2"><Lock className="w-5 h-5" /> {hasVault ? '🔓 Unlock Your Vault' : '🆕 Create Your Vault'}</div>
            <div className="text-xs text-slate-300">{hasVault ? 'Enter your PIN / passphrase to unlock your encrypted data.' : 'Set a strong PIN or passphrase (min 4 chars). This is the ONLY way to decrypt your data — we do NOT store it anywhere. Losing it = losing data forever.'}</div>
            <input type="password" value={passphrase} onChange={(e) => setPassphrase(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && unlockVault()} placeholder={hasVault ? 'Your PIN / Passphrase *' : 'New PIN / Passphrase *'} data-testid="vault-pass" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" autoFocus />
            {!hasVault && (
              <input type="password" value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && unlockVault()} placeholder="Confirm PIN / Passphrase *" data-testid="vault-pass-confirm" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
            )}
            <button onClick={unlockVault} data-testid="vault-unlock-btn" className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-fuchsia-500 to-purple-500 text-white font-black flex items-center justify-center gap-1"><Unlock className="w-4 h-4" /> {hasVault ? 'Unlock Vault' : 'Create Vault'}</button>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
              <div className="text-sm">🔓 <b>Unlocked</b> · {items.length} secrets · {docs.length} docs</div>
              <button onClick={lockVault} data-testid="vault-lock-btn" className="px-3 py-1.5 rounded-lg bg-white/10 text-xs font-black flex items-center gap-1"><Lock className="w-3 h-3" /> Lock</button>
            </div>
            {/* Tabs */}
            <div className="flex gap-1 mb-3 flex-wrap" data-testid="vault-tabs">
              {[['secrets', '🔑 Secrets & Passwords'], ['docs', '📄 Documents (DigiLocker)']].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`vault-tab-${k}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-black ${tab === k ? 'bg-fuchsia-500 text-white' : 'bg-white/5 text-slate-300'}`}>{l}</button>
              ))}
            </div>

          {tab === 'secrets' && <>
            {/* Add form */}
            <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4 space-y-2 mb-4">
              <div className="text-xs uppercase tracking-widest text-fuchsia-300 font-black flex items-center gap-1"><Plus className="w-3 h-3" /> Add New Item</div>
              <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Title (e.g. My Bitcoin Seed)" data-testid="vault-new-title" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
              <select value={newCategory} onChange={(e) => setNewCategory(e.target.value)} data-testid="vault-new-cat" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <textarea value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder="Secret value / seed phrase / password" data-testid="vault-new-value" rows={3} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm font-mono" />
              <button onClick={addItem} data-testid="vault-save-item" className="w-full px-4 py-2 rounded-lg bg-emerald-500 text-black font-black">Save (Encrypted)</button>
            </div>

            {/* Items grouped */}
            <div className="space-y-3">
              {Object.keys(grouped).map((cat) => (
                <div key={cat} className="rounded-2xl border border-slate-700 bg-slate-900/60 p-3">
                  <div className="text-xs uppercase tracking-widest text-fuchsia-300 font-black mb-2">{cat} ({grouped[cat].length})</div>
                  {grouped[cat].map((it) => (
                    <div key={it.id} className="p-2 rounded-lg bg-slate-800 mb-2" data-testid={`vault-item-${it.id}`}>
                      <div className="flex justify-between items-center flex-wrap gap-1">
                        <b>{it.title}</b>
                        <div className="flex gap-1">
                          <button onClick={() => setShowValue((s) => ({ ...s, [it.id]: !s[it.id] }))} className="p-1 rounded bg-white/10">{showValue[it.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}</button>
                          <button onClick={() => copyValue(it.value)} className="p-1 rounded bg-white/10"><Copy className="w-3 h-3" /></button>
                          <button onClick={() => delItem(it.id)} className="p-1 rounded bg-rose-500/40"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      </div>
                      <div className="text-xs font-mono break-all mt-1 text-fuchsia-200">
                        {showValue[it.id] ? it.value : '••••••••••••'}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
              {items.length === 0 && <div className="text-center text-sm text-slate-400 py-6">Vault empty — add your first item ↑</div>}
            </div>
          </>}

          {tab === 'docs' && <>
            <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4 space-y-2 mb-4">
              <div className="text-xs uppercase tracking-widest text-fuchsia-300 font-black flex items-center gap-1"><Plus className="w-3 h-3" /> Add Document Photo</div>
              <input value={newDocTitle} onChange={(e) => setNewDocTitle(e.target.value)} placeholder="Document Title (e.g. My Aadhaar Card)" data-testid="vault-doc-title" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
              <select value={newDocType} onChange={(e) => setNewDocType(e.target.value)} data-testid="vault-doc-type" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm">
                {DOC_TYPES.map((d) => <option key={d.id} value={d.id}>{d.emoji} {d.label}</option>)}
              </select>
              <input type="file" accept="image/*" onChange={onPickDocImg} data-testid="vault-doc-file" className="text-xs w-full" />
              {newDocImg && <img src={newDocImg} alt="preview" className="w-full max-h-40 object-contain rounded-lg border border-slate-700" />}
              <div className="text-[10px] text-amber-200 opacity-80">⚠️ Max 3 MB per doc. Images stored encrypted in browser only.</div>
              <button onClick={addDoc} data-testid="vault-doc-save" disabled={!newDocTitle || !newDocImg} className="w-full px-4 py-2 rounded-lg bg-emerald-500 text-black font-black disabled:opacity-40">Save Document (Encrypted)</button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {docs.map((d) => {
                const dt = DOC_TYPES.find((x) => x.id === d.doc_type) || DOC_TYPES[DOC_TYPES.length - 1];
                return (
                  <div key={d.id} className="rounded-xl border border-fuchsia-400/30 bg-fuchsia-500/5 p-3" data-testid={`vault-doc-${d.id}`}>
                    <div className="flex justify-between items-center mb-2">
                      <div><span className="text-lg">{dt.emoji}</span> <b>{d.title}</b></div>
                      <button onClick={() => delDoc(d.id)} className="p-1 rounded bg-rose-500/40"><Trash2 className="w-3 h-3" /></button>
                    </div>
                    <div className="text-[10px] opacity-70 mb-2">{dt.label}</div>
                    <img src={d.image} alt={d.title} onClick={() => setPreviewDoc(d)} className="w-full h-32 object-cover rounded-lg cursor-pointer hover:scale-[1.01] transition" />
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => shareDoc(d)} data-testid={`vault-doc-share-${d.id}`} className="flex-1 px-2 py-1.5 rounded-lg bg-green-500 text-black text-[11px] font-black flex items-center justify-center gap-1"><Share2 className="w-3 h-3" /> Share</button>
                      <button onClick={() => downloadDoc(d)} data-testid={`vault-doc-download-${d.id}`} className="flex-1 px-2 py-1.5 rounded-lg bg-cyan-500 text-black text-[11px] font-black flex items-center justify-center gap-1"><Download className="w-3 h-3" /> Save</button>
                    </div>
                  </div>
                );
              })}
              {docs.length === 0 && <div className="col-span-full text-center text-sm text-slate-400 py-6">No documents saved. Add Aadhaar/PAN/Voter ID etc. ↑</div>}
            </div>
          </>}

          {/* Preview modal */}
          {previewDoc && (
            <div className="fixed inset-0 bg-black/95 z-[150] flex items-center justify-center p-3" onClick={() => setPreviewDoc(null)}>
              <img src={previewDoc.image} alt={previewDoc.title} className="max-w-full max-h-full rounded-lg" />
            </div>
          )}

          {/* Forgot passphrase notice */}
          <div className="mt-3 rounded-lg border border-slate-700 bg-slate-900/60 p-2 text-[10px] text-slate-400 flex items-center gap-1">
            <KeyRound className="w-3 h-3 text-amber-300" />
            <span>Forgot passphrase? Admin ko contact karo — <b>Admin Vault Reset</b> se fresh start milega (purana data delete ho jayega).</span>
          </div>

            <div className="mt-6 p-3 rounded-lg border border-amber-400/40 bg-amber-500/10 text-[10px] text-amber-100">
              ⚠️ <b>Warning:</b> Vault data is stored in THIS browser only. Clear browser data / logout = data lost. Backup manually via Copy button. Passphrase can&apos;t be recovered.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
